/**
 * Base Event Builder
 * Provides common functionality for building all tracking events
 */

import snakecaseKeys from 'snakecase-keys';
import { BaseEvent, GaEventType } from '../types';

/**
 * Get AB test variant from the body data attribute
 * @todo consider refactoring as the tracking package should not depend on DOM of a foreign app
 */
export function getAbTestVariantFromBody(): string | null {
    if (typeof document === 'undefined' || !document.body) {
        return null;
    }

    return document.body.getAttribute('data-ab');
}

/**
 * Builds a base event with common properties that all events should have
 * - Converts all options from camelCase to snake_case
 * - Automatically adds A/B test variant if available
 * - Ensures consistent event structure across all event types
 *
 * @param eventType The type of event to build
 * @param options The event options in camelCase
 * @returns The built event with snake_case properties and common fields
 */
export function buildBaseEvent<T extends BaseEvent>(
    eventType: GaEventType,
    options: Record<string, any>
): T {
    // Convert all options to snake_case
    const snakeOptions = snakecaseKeys(options as any, { deep: true });

    const event = {
        event: eventType,
        ...snakeOptions,
    } as T;

    // Always add A/B test variant if available
    const abTestVariant = getAbTestVariantFromBody();
    if (abTestVariant) {
        event.ab_test_variant = abTestVariant;
    }

    return event;
}